import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/posts");
        setPosts(data);
      } catch {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const featured = posts.find((p) => p.featured) || posts[0];
  const rest = posts.filter((p) => p.slug !== featured?.slug);

  const jsonLd = useMemo(() => ([
    {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Insights — Spin Labs",
      url: "https://spinlabs.in/insights",
      description: "Shipping notes, engineering deep-dives and opinionated takes from the Spin Labs team.",
      publisher: { "@type": "Organization", name: "Spin Labs", url: "https://spinlabs.in" },
      blogPost: posts.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        url: `https://spinlabs.in/insights/${p.slug}`,
        image: p.cover_img,
        author: { "@type": "Person", name: p.author },
        datePublished: p.published_at || p.created_at,
        articleSection: p.category,
      })),
    },
    breadcrumbJsonLd([
      { name: "Home", path: "/" },
      { name: "Insights", path: "/insights" },
    ]),
  ]), [posts]);

  return (
    <div data-testid="blog-page">
      <Seo
        title="Insights — Engineering, AI, Product Notes"
        path="/insights"
        description="Shipping notes, engineering deep-dives and opinionated takes from the Spin Labs team on AI, software, mobile and SaaS."
        keywords="Spin Labs blog, AI engineering blog, software blog, SaaS blog, mobile app blog"
        jsonLd={jsonLd}
      />
      <section className="relative pt-36 pb-14 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="spotlight w-[600px] h-[600px] bg-[#FF00FF]/15 top-0 right-10" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="tag-chip mb-6">Insights</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-black tracking-[-0.04em] leading-[0.9] text-balance max-w-5xl">
              Notes from the <span className="gradient-text">workshop</span>.
            </h1>
            <p className="mt-6 max-w-2xl text-white/60 leading-relaxed">
              Shipping notes, engineering deep-dives, and opinionated takes from the Spin Labs team.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="relative pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="h-96 flex items-center justify-center text-white/40">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <>
              {featured && (
                <Link
                  to={`/insights/${featured.slug}`}
                  data-testid={`post-featured-${featured.slug}`}
                  className="group relative block overflow-hidden rounded-3xl border border-white/10 hover:border-[#00F0FF]/40 transition-colors"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className="relative aspect-[16/10] lg:aspect-auto lg:min-h-[420px]">
                      <img src={featured.cover_img} alt={featured.title} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity" />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/80" />
                    </div>
                    <div className="relative p-8 lg:p-14 flex flex-col justify-center bg-[#0A0A0C]">
                      <div className="font-mono-neo text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] mb-4">{featured.category} · {featured.read_time}</div>
                      <h2 className="font-display text-3xl lg:text-5xl font-black tracking-tighter text-balance">{featured.title}</h2>
                      <p className="mt-5 text-white/70 leading-relaxed">{featured.excerpt}</p>
                      <div className="mt-8 flex items-center gap-3 text-sm">
                        <span className="text-white/50">{featured.author}</span>
                        <ArrowUpRight className="w-4 h-4 text-[#00F0FF] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((p, i) => (
                  <motion.div
                    key={p.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5, delay: i * 0.06 }}
                  >
                    <Link
                      to={`/insights/${p.slug}`}
                      data-testid={`post-card-${p.slug}`}
                      className="group block h-full rounded-2xl overflow-hidden border border-white/10 hover:border-[#00F0FF]/40 transition-colors bg-[#0A0A0C]"
                    >
                      <div className="relative aspect-[16/9] overflow-hidden">
                        <img src={p.cover_img} alt={p.title} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" />
                      </div>
                      <div className="p-6">
                        <div className="font-mono-neo text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] mb-3">{p.category} · {p.read_time}</div>
                        <h3 className="font-display text-xl font-bold tracking-tight leading-snug">{p.title}</h3>
                        <p className="mt-3 text-sm text-white/60 leading-relaxed line-clamp-2">{p.excerpt}</p>
                        <div className="mt-5 pt-5 border-t border-white/10 flex items-center justify-between text-xs">
                          <span className="text-white/50">{p.author}</span>
                          <ArrowUpRight className="w-4 h-4 text-[#00F0FF] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
